import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Skeleton,
  Switch,
  FormControlLabel,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import usePackagingCheckApi, { getApiErrorMessage } from "../../components/PackagingCheck/usePackagingCheckApi";
import { CHANGE_STYLES } from "../../components/PackagingCheck/results/SectionComparison";
import ComparisonExplorer from "../../components/PackagingCheck/results/ComparisonExplorer";
import AutoChecksPanel from "../../components/PackagingCheck/results/AutoChecksPanel";
import ResultSummary from "../../components/PackagingCheck/results/ResultSummary";
import TabBar from "../../components/PackagingCheck/TabBar";
import { checkTitle } from "../../components/PackagingCheck/RecentChecks";
import CorrectionsList from "../../components/PackagingCheck/results/CorrectionsList";
import UnrequestedList from "../../components/PackagingCheck/results/UnrequestedList";
import { FilterChip } from "../../components/PackagingCheck/results/CorrectionsList";
import {
  BORDER,
  BRAND,
  BRAND_DARK,
  BRAND_SOFT,
  SEVERITIES,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  VERSIONS,
  formatDateTime,
} from "../../components/PackagingCheck/constants";

// Long file names keep their start and end ("DR OR_SERUM…6.8.26.pdf")
const middleEllipsis = (name = "", max = 46) =>
  name.length <= max ? name : `${name.slice(0, Math.ceil(max / 2) - 1)}…${name.slice(-Math.floor(max / 2))}`;

// A version date written in the file name, e.g. "…_6.8.26.pdf"
const dateInName = (name = "") => name.match(/(\d{1,2}\.\d{1,2}\.\d{2,4})/)?.[1];

const VersionCard = ({ version, name }) => {
  const date = dateInName(name);
  return (
    <Box
      sx={{
        minWidth: 0,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        bgcolor: "#fff",
        border: `1px solid ${BORDER}`,
        borderTop: `3px solid ${version.color}`,
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: version.key === "updated" ? BRAND_DARK : TEXT_PRIMARY }}>
        {version.label}
        {date && ` · ${date}`}
      </Typography>
      <Typography noWrap title={name} sx={{ fontSize: 13, color: TEXT_SECONDARY, mt: 0.25 }}>
        <bdi dir="ltr">{middleEllipsis(name)}</bdi>
      </Typography>
    </Box>
  );
};

const ALL_SEVERITIES = SEVERITIES.map((s) => s.key);

// Colour legend, shown inline next to the severity filters
const Legend = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
    {Object.entries(CHANGE_STYLES).map(([key, style]) => (
      <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          component="span"
          sx={{
            px: 0.75,
            borderRadius: "4px",
            bgcolor: style.bg,
            color: style.color === "inherit" ? TEXT_PRIMARY : style.color,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: style.strike ? "line-through" : "none",
            borderBottom: style.dotted ? "2px dotted #667085" : "none",
          }}
        >
          טקסט
        </Box>
        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>{style.label}</Typography>
      </Box>
    ))}
  </Box>
);

const LAYOUTS = [
  { key: "menu", label: "תפריט צד", icon: <ViewSidebarOutlinedIcon fontSize="small" /> },
  { key: "all", label: "הכל ברצף", icon: <ViewAgendaOutlinedIcon fontSize="small" /> },
];

const TABS = [
  { key: "corrections", label: "תיקונים נדרשים" },
  { key: "unrequested", label: "שינויים שלא נדרשו" },
  { key: "checks", label: "בדיקות אוטומטיות" },
  { key: "comparison", label: "השוואה מלאה" },
];
const POLL_INTERVAL_MS = 3000;

const PackagingCheckResultPage = () => {
  const { checkId } = useParams();
  const navigate = useNavigate();
  const api = usePackagingCheckApi();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(() => new Set(ALL_SEVERITIES));
  const [showAll, setShowAll] = useState(false);
  const [layout, setLayout] = useState("menu");
  const [statusFilter, setStatusFilter] = useState("all");
  const [focusedFindingIds, setFocusedFindingIds] = useState(null);

  const { data: check, isLoading, error } = useQuery({
    queryKey: ["packagingCheck", checkId],
    queryFn: () => api.getCheck(checkId),
    // Corrections control runs in the background — refresh until it ends
    refetchInterval: (query) => (query.state.data?.status === "controlling" ? POLL_INTERVAL_MS : false),
  });

  const rerun = useMutation({
    mutationFn: () => api.rerunCorrectionsControl(checkId),
    onSuccess: (updated) => {
      queryClient.setQueryData(["packagingCheck", checkId], updated);
      queryClient.invalidateQueries({ queryKey: ["packagingChecks"] });
    },
  });

  const comparison = check?.comparison;
  const findingsById = useMemo(
    () => new Map((comparison?.findings || []).map((finding) => [finding.id, finding])),
    [comparison],
  );

  // Without a corrections list there is nothing to control and every change
  // already shows in the comparison, so only the comparison and checks remain
  const hasCorrections = check?.corrections?.length > 0;
  const visibleTabs = hasCorrections
    ? TABS
    : ["comparison", "checks"].map((key) => TABS.find((t) => t.key === key));
  const defaultTab = hasCorrections ? "corrections" : "comparison";
  const tab = visibleTabs.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : defaultTab;
  const setTab = (key) => setSearchParams({ tab: key }, { replace: true });

  // The comparison scrolls to the focused change; the highlight fades after a while
  useEffect(() => {
    if (!focusedFindingIds) return undefined;
    const timer = setTimeout(() => setFocusedFindingIds(null), 3000);
    return () => clearTimeout(timer);
  }, [focusedFindingIds]);

  const showFindings = (ids) => {
    setSelected(new Set(ALL_SEVERITIES));
    setFocusedFindingIds(new Set(ids));
    setTab("comparison");
  };

  const toggleSeverity = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visibleSections = (comparison?.sections || []).filter(
    (section) =>
      showAll ||
      section.findingIds.some((id) => selected.has(findingsById.get(id)?.severity)),
  );

  const control = check?.correctionsControl;
  const unrequestedCount = control?.unrequestedFindingIds?.length || 0;
  const openCorrections = (control?.summary?.not_done || 0) + (control?.summary?.partially_done || 0);
  const tabCounts = {
    corrections: check?.corrections?.length
      ? { value: check.corrections.length, alert: openCorrections > 0, color: "#dc2626" }
      : null,
    unrequested: control ? { value: unrequestedCount, alert: unrequestedCount > 0, color: "#d97706" } : null,
    checks: check?.autoChecks
      ? { value: check.autoChecks.summary.total, alert: check.autoChecks.summary.critical > 0, color: "#dc2626" }
      : null,
    comparison: comparison ? { value: comparison.summary.total } : null,
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 10, sm: 11 }, maxWidth: 1400, mx: "auto" }}>
      <Button
        startIcon={<ArrowForwardIcon />}
        onClick={() => navigate("/packaging-check")}
        sx={{ color: TEXT_SECONDARY, fontWeight: 600, mb: 1.5 }}
      >
        חזרה לבדיקות
      </Button>

      {isLoading && <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {getApiErrorMessage(error, "לא ניתן לטעון את הבדיקה")}
        </Alert>
      )}

      {check && (
        <Fade in timeout={400}>
          <Box>
            {/* Header — name and details, the report button, then the two versions */}
            <Box component="header" sx={{ pb: 2, mb: 2, borderBottom: `1px solid ${BORDER}` }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px 24px" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h1" dir="auto" sx={{ fontSize: 24, fontWeight: 700, color: TEXT_PRIMARY, textAlign: "start" }}>
                    {checkTitle(check)}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mt: 0.25 }}>
                    {[
                      formatDateTime(check.createdAt),
                      check.createdBy?.name || check.createdBy?.email,
                      check.title && check.product?.name,
                      check.product?.sku && `מק"ט ${check.product.sku}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </Box>
                {/* Viewing and downloading (PDF / Excel) both live on the report page */}
                {comparison?.sections && (
                  <Button
                    variant="outlined"
                    startIcon={<DescriptionRoundedIcon />}
                    onClick={() => navigate(`/packaging-check/${checkId}/report`)}
                    sx={{
                      minHeight: 40,
                      px: 2.25,
                      borderRadius: 1.5,
                      fontSize: 15,
                      fontWeight: 600,
                      color: BRAND_DARK,
                      borderColor: BRAND,
                      bgcolor: "#fff",
                      "&:hover": { borderColor: BRAND_DARK, bgcolor: BRAND_SOFT },
                    }}
                  >
                    צפייה בדוח
                  </Button>
                )}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 2 }}>
                <VersionCard version={VERSIONS.previous} name={check.files?.previous?.originalName} />
                <VersionCard version={VERSIONS.updated} name={check.files?.updated?.originalName} />
              </Box>
            </Box>
            {comparison?.sections && (
              <Box sx={{ mb: 2 }}>
                <ResultSummary
                  check={check}
                  onStatusClick={(status) => {
                    setStatusFilter(status);
                    setTab("corrections");
                  }}
                  onRerun={() => rerun.mutate()}
                  rerunning={rerun.isPending}
                />
              </Box>
            )}

            {!comparison?.sections ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                הבדיקה נפתחה לפני שנוספה תצוגת ההשוואה — כדי לראות את התוצאות יש לפתוח בדיקה חדשה עם אותם קבצים.
              </Alert>
            ) : (
              <>
                {rerun.error && (
                  <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
                    {getApiErrorMessage(rerun.error, "לא ניתן להריץ מחדש")}
                  </Alert>
                )}

                <Box sx={{ mt: 1, mb: 2 }}>
                  <TabBar
                    tabs={visibleTabs}
                    tab={tab}
                    onChange={setTab}
                    counts={tabCounts}
                  />
                </Box>

                <Fade in key={tab} timeout={250}>
                  <Box>
                    {tab === "corrections" &&
                      (check.status === "controlling" && check.corrections?.length ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {check.corrections.slice(0, 4).map((_, index) => (
                            <Skeleton key={index} variant="rounded" height={110} sx={{ borderRadius: 2 }} />
                          ))}
                        </Box>
                      ) : (
                        <CorrectionsList
                          check={check}
                          statusFilter={statusFilter}
                          onStatusFilter={setStatusFilter}
                          onShowFindings={showFindings}
                        />
                      ))}

                    {tab === "unrequested" && (
                      <UnrequestedList check={check} findingsById={findingsById} onShowFindings={showFindings} />
                    )}

                    {tab === "checks" &&
                      (check.autoChecks ? (
                        <AutoChecksPanel autoChecks={check.autoChecks} />
                      ) : (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          הבדיקה נפתחה לפני שנוספו הבדיקות האוטומטיות.
                        </Alert>
                      ))}

                    {tab === "comparison" && (
                      <>
                        {comparison.mode === "document" && (
                          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                            מבנה האריזה השתנה באופן מהותי ולא ניתן היה להתאים סעיף מול סעיף — ההשוואה בוצעה על כל
                            המסמך בוודאות נמוכה.
                          </Alert>
                        )}

                        <Box
                          sx={{
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                            {SEVERITIES.filter(({ key }) => comparison.summary[key] > 0).map(({ key, label, color }) => (
                              <FilterChip
                                key={key}
                                label={label}
                                count={comparison.summary[key]}
                                color={color}
                                active={selected.has(key)}
                                onClick={() => toggleSeverity(key)}
                              />
                            ))}
                          </Box>
                          <Legend />
                          <Box sx={{ flex: 1 }} />
                          <FormControlLabel
                            control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
                            label={<Typography sx={{ fontSize: 13 }}>הצגת כל הסעיפים</Typography>}
                          />
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={layout}
                            onChange={(_, value) => value && setLayout(value)}
                            aria-label="תצוגת ההשוואה"
                          >
                            {LAYOUTS.map(({ key, label, icon }) => (
                              <ToggleButton
                                key={key}
                                value={key}
                                sx={{
                                  gap: 0.75,
                                  px: 1.5,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: TEXT_SECONDARY,
                                  "&.Mui-selected": { color: BRAND_DARK, bgcolor: BRAND_SOFT },
                                  "&.Mui-selected:hover": { bgcolor: BRAND_SOFT },
                                }}
                              >
                                {icon}
                                {label}
                              </ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                        </Box>

                        {visibleSections.length === 0 ? (
                          <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: `1px solid ${BORDER}`, textAlign: "center" }}>
                            <Typography color={TEXT_SECONDARY}>
                              {comparison.summary.total === 0
                                ? "לא נמצאו הבדלים בין הגרסאות"
                                : "אין ממצאים ברמות החומרה שנבחרו"}
                            </Typography>
                          </Paper>
                        ) : (
                          <ComparisonExplorer
                            sections={visibleSections}
                            findingsById={findingsById}
                            visibleSeverities={selected}
                            focusedFindingIds={focusedFindingIds}
                            layout={layout}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </Fade>
                <Typography sx={{ mt: 3, pt: 1.5, borderTop: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_SECONDARY }}>
                  מה לא נבדק: עיצוב (צבע, גופן, מיקום), טקסט שהומר לעקומות ופרשנות רגולטורית. הבדיקה אינה מהווה אישור לקובץ הדפוס.
                </Typography>
              </>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default PackagingCheckResultPage;
